import { useRouter } from 'next/router'
import { type FC, useCallback, useEffect, useState } from 'react'

// This functionality supposed to be implemented using Google Tag Manager.
// However, GTM only loads after user give consents.
// Until we load GTM initially without any consents (or existing consents), independely of the consents process,
// we implement the functionality in the codebase as React Component.
// This code does not follow our coding standards.
//
// TODO: Either with GTM and move the functionality to GTM or refactor this to comply standards.
const QrCodeOverlay: FC = () => {
  const router = useRouter()
  const { query, locale, isReady } = router

  const [mounted, setMounted] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [visible, setVisible] = useState(true)

  // Mark component as mounted and detect mobile devices.
  useEffect(() => {
    setMounted(true)
    if (typeof navigator !== 'undefined') {
      setIsMobile(
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent)
      )
    }
  }, [])

  // Escape key listener.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        closeOverlay()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const closeOverlay = (): void => {
    const overlay = document.getElementById('qr-code-overlay')
    if (overlay) {
      overlay.style.opacity = '0'
      if (overlay.firstChild) {
        const firstChild = overlay.firstChild as HTMLElement
        firstChild.style.transform = 'scale(0.95)'
      }
      setTimeout(() => {
        setVisible(false)
      }, 300)
    }
  }

  const qrCodeUrl = query.qr_code_url
  const isGerman = locale === 'de-de'
  const headlineText = isGerman ? 'Scannen, um App zu öffnen' : 'Scan to open in app'
  const sublineText = isGerman
    ? 'Was du suchst ist nur in der Getsafe-App verfügbar.'
    : "What you're looking for is only available in the Getsafe app."
  const closeText = isGerman ? 'Schließen' : 'Close'

  const shouldRender = isReady && mounted && qrCodeUrl && !isMobile && visible

  // Callback ref to generate QR code once the element mounts.
  const qrWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        return
      }
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js'
      script.async = true
      script.onload = () => {
        try {
          const qrCodeGenerator = (window as any).qrcode
          if (qrCodeGenerator) {
            const qrCodeDiv = document.createElement('div')
            const qr = new qrCodeGenerator(0, 'H')
            qr.addData(decodeURIComponent(String(qrCodeUrl)))
            qr.make()
            qrCodeDiv.innerHTML = qr.createImgTag(4)
            node.innerHTML = ''
            node.append(qrCodeDiv)
          }
        } catch (error) {
          console.error('QR code generation error:', error)
        }
      }
      document.body.append(script)
      return () => {
        document.body.removeChild(script)
      }
    },
    [qrCodeUrl]
  )

  // Allow keyboard users to trigger closing via Enter or Space.
  const handleOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      closeOverlay()
    }
  }

  return shouldRender ? (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); }
          to { transform: scale(1); }
        }
      `}</style>
      <div
        id="qr-code-overlay"
        role="button"
        tabIndex={0}
        onKeyDown={handleOverlayKeyDown}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.3s ease',
        }}
        onClick={(e) => {
          if ((e.target as Element).id === 'qr-code-overlay') {
            closeOverlay()
          }
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            padding: '40px 20px',
            borderRadius: '16px',
            textAlign: 'center',
            width: '90%',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            animation: 'scaleIn 0.3s ease',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            position: 'relative',
          }}
        >
          <h2
            style={{
              fontSize: '24px',
              fontWeight: 600,
              margin: '0 0 8px 0',
              color: '#000',
              textAlign: 'center',
            }}
          >
            {headlineText}
          </h2>
          <p
            style={{
              fontSize: '16px',
              color: '#333',
              margin: '0 0 32px 0',
              textAlign: 'center',
              maxWidth: '280px',
            }}
          >
            {sublineText}
          </p>
          <div
            ref={qrWrapperRef}
            style={{
              width: '232px',
              height: '232px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
          <button
            onClick={closeOverlay}
            style={{
              position: 'absolute',
              right: '16px',
              top: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#000',
              fontSize: '24px',
              lineHeight: 1,
              padding: 0,
            }}
            aria-label={closeText}
          >
            ×
          </button>
        </div>
      </div>
    </>
  ) : null
}

export default QrCodeOverlay
